import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  model,
  OnInit,
  Optional,
  output,
  signal,
} from '@angular/core';
import { ControlContainer, FormsModule, NgForm, ValidatorFn } from '@angular/forms';
import { TranslocoDirective } from '@jsverse/transloco';
import { createMask } from '@ngneat/input-mask';
import { AlertComponent } from '@pedix-workspace/angular-ui-alert';
import { ButtonComponent } from '@pedix-workspace/angular-ui-button';
import { InputTextComponent } from '@pedix-workspace/angular-ui-form-reactive';
import {
  IconCheckComponent,
  IconInfoComponent,
  IconLoadingComponent,
} from '@pedix-workspace/angular-ui-icons';
import { TransferDetailsCardComponent } from '../../transfer-details-card/transfer-details-card.component';

import {
  EndOrderDetailDigitalPayment,
  formatPayerShortName,
  PaymentMethodTransfer,
  ResolvedTransferPayer,
  TransferPayer,
} from '@pedix-workspace/utils';
import { ApiV1_AvailableIntegration } from '@pedix-workspace/api-v1';
import {
  BankTransferErrorCode,
  BankTransferRequestError,
  BankTransferService,
  GenerateCVUResponse,
  isCachedForReference,
} from './bank-transfer.service';

const PAYER_DOCUMENT_PATTERN = /^\d{7,8}$/;
const PAYER_LOOKUP_DEBOUNCE_MS = 400;
const SHORT_PAYER_DOCUMENT_DEBOUNCE_MS = 1500;
const SHORT_PAYER_DOCUMENT_LENGTH = 7;

export const PAYER_DOCUMENT_MASK = createMask<string>({
  alias: 'numeric',
  groupSeparator: '.',
  radixPoint: ',',
  digits: 0,
  rightAlign: false,
  allowMinus: false,
  placeholder: '',
  max: 99999999,
  autoUnmask: true,
});

type PayerFieldError = 'payerNotFound' | 'invalidDocument' | 'payerNotChangeable';

const PAYER_FIELD_ERRORS: Record<BankTransferErrorCode, PayerFieldError> = {
  payer_not_found: 'payerNotFound',
  invalid_document: 'invalidDocument',
  payer_not_changeable: 'payerNotChangeable',
  invalid_payer_token: 'payerNotChangeable',
};

export function normalizePayerDocument(value: string | null | undefined): string {
  return (value ?? '').replace(/\D/g, '');
}

export function formatPayerDocument(document: string): string {
  return document.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

export type TransferCVUData = {
  cvu: string;
  accountHolder: string;
  alias?: string;
  provider: 'chytapay' | 'cucuru';
};

/**
 * Component that handles bank transfer CVU generation and amount updates.
 * Encapsulates all transfer-related logic following the shipping option pattern.
 *
 * Features:
 * - Generates CVU when payment method is TRANSFER with integration available
 * - Uses cached CVU from service to prevent orphan requests
 * - Uses transferReferenceId instead of orderId
 */
@Component({
  selector: 'pxw-end-order-bank-transfer-fields',
  templateUrl: './end-order-bank-transfer-fields.component.html',
  styleUrls: ['./end-order-bank-transfer-fields.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoDirective,
    FormsModule,
    AlertComponent,
    ButtonComponent,
    InputTextComponent,
    IconCheckComponent,
    IconInfoComponent,
    IconLoadingComponent,
    TransferDetailsCardComponent,
  ],
  viewProviders: [
    {
      provide: ControlContainer,
      deps: [[Optional, NgForm]],
      useFactory: (ngForm: NgForm) => ngForm,
    },
  ],
})
export class EndOrderBankTransferFieldsComponent implements OnInit {
  private readonly bankTransferService = inject(BankTransferService);
  private readonly destroyRef = inject(DestroyRef);

  /** AbortController to cancel pending requests on destroy */
  private abortController: AbortController | null = null;

  // Inputs
  readonly paymentMethod = input.required<PaymentMethodTransfer>();
  readonly finalAmount = input.required<number>();
  readonly establishmentId = input.required<string>();
  readonly customerName = input.required<string>();
  readonly availableIntegrations = input.required<ApiV1_AvailableIntegration[]>();
  readonly showTransferDetails = input<boolean>(false);
  readonly transferReferenceId = input<string>();
  readonly payerDocumentEnabled = input<boolean>(false);
  readonly isFormSubmitted = input<boolean>(false);
  readonly transferPayer = model<TransferPayer | null>(null);

  // Outputs
  readonly cvuDataChange = output<TransferCVUData | null>();
  readonly digitalPaymentChange = output<EndOrderDetailDigitalPayment | null>();

  // Internal state
  readonly cvuData = signal<TransferCVUData | null>(null);
  readonly cvuLoading = signal(false);
  readonly cvuError = signal<string | null>(null);

  readonly payerDocumentMask = PAYER_DOCUMENT_MASK;
  readonly payerDocumentInput = signal<string | null>(null);
  readonly payerDocument = computed(
    () => normalizePayerDocument(this.payerDocumentInput()) || null,
  );
  readonly resolvedPayer = signal<ResolvedTransferPayer | null>(null);
  readonly payerError = signal<PayerFieldError | null>(null);
  readonly editingPayer = signal(true);
  private payerLookupTimeout: ReturnType<typeof setTimeout> | null = null;

  /**
   * Check if there's a transfer integration available
   */
  readonly hasTransferIntegration = computed(() => {
    return this.availableIntegrations().some(i => i.types?.includes('bank-transfer'));
  });

  /**
   * Get the transfer integration provider (chytapay or cucuru)
   */
  readonly transferIntegrationProvider = computed<'chytapay' | 'cucuru' | null>(() => {
    const integration = this.availableIntegrations().find(i => i.types?.includes('bank-transfer'));
    if (!integration) return null;

    const provider = integration.provider;
    if (provider === 'chytapay' || provider === 'cucuru') {
      return provider;
    }
    return null;
  });

  readonly payerConciliationEnabled = computed(
    () => this.payerDocumentEnabled() && this.transferIntegrationProvider() === 'chytapay',
  );

  readonly showPayerField = computed(() => this.payerConciliationEnabled() && this.editingPayer());

  readonly showPayerAlert = computed(
    () => this.payerConciliationEnabled() && !this.editingPayer() && !!this.transferPayer(),
  );

  readonly payerFieldValidator = computed<ValidatorFn>(() => {
    const payerError = this.payerError();
    const resolvedPayer = this.resolvedPayer();
    const payerLookupFailed = !!this.cvuError();

    return control => {
      const document = normalizePayerDocument(control.value);

      if (!document) {
        return null;
      }
      if (payerError) {
        return { [payerError]: true };
      }
      if (!PAYER_DOCUMENT_PATTERN.test(document)) {
        return { invalidDocument: true };
      }
      if (resolvedPayer?.document !== document && !payerLookupFailed) {
        return { required: true };
      }
      return null;
    };
  });

  readonly resolvedPayerForDocument = computed(() => {
    const resolvedPayer = this.resolvedPayer();

    return resolvedPayer?.document === this.payerDocument() ? resolvedPayer : null;
  });

  readonly payerLookupPending = computed(() => {
    const document = this.payerDocument();

    return (
      !!document &&
      PAYER_DOCUMENT_PATTERN.test(document) &&
      !this.resolvedPayerForDocument() &&
      !this.payerError() &&
      !this.cvuError()
    );
  });

  readonly displayPayerFieldErrors = computed(
    () => !!this.payerError() || (this.isFormSubmitted() && !this.payerLookupPending()),
  );

  readonly payerFieldHelperKey = computed(() => {
    const resolvedPayer = this.resolvedPayerForDocument();

    if (!resolvedPayer) {
      return 'endOrder.transferPayerHelper';
    }
    return resolvedPayer.status === 'unverified'
      ? 'endOrder.transferPayerHelperUnverified'
      : 'endOrder.transferPayerHelperVerified';
  });

  readonly resolvedPayerShortName = computed(() => {
    const name = this.resolvedPayerForDocument()?.name;
    return name ? formatPayerShortName(name) : null;
  });

  readonly storedPayerName = computed(() => {
    const name = this.transferPayer()?.name;
    return name ? formatPayerShortName(name) : null;
  });

  readonly storedPayerDocument = computed(() =>
    formatPayerDocument(this.transferPayer()?.document ?? ''),
  );

  /**
   * Check if payment method has manual transfer details
   */
  readonly hasManualTransferDetails = computed(() => {
    const pm = this.paymentMethod();
    return (
      !this.hasTransferIntegration() &&
      pm.transferDetails &&
      (pm.transferDetails.cvu || pm.transferDetails.alias)
    );
  });

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.abortController?.abort();
      this.clearPayerLookupTimeout();
      this.digitalPaymentChange.emit(null);
    });
  }

  ngOnInit(): void {
    if (!this.hasTransferIntegration()) {
      return;
    }

    if (this.payerConciliationEnabled()) {
      this.initPayerConciliation();
      return;
    }

    const cachedData = this.bankTransferService.getCachedCvuData(this.establishmentId());
    if (cachedData && isCachedForReference(cachedData, this.transferReferenceId())) {
      this.emitCvu(cachedData);
      return;
    }

    this.generateCVU();
  }

  private emitCvu(result: GenerateCVUResponse): void {
    const cvuData: TransferCVUData = {
      cvu: result.cvu,
      accountHolder: result.accountHolder,
      alias: result.alias,
      provider: result.provider,
    };

    this.cvuData.set(cvuData);
    this.cvuDataChange.emit(cvuData);

    const digitalPayment: EndOrderDetailDigitalPayment = {
      type: 'transfer',
      status: 'PENDING',
      provider: result.provider,
      providerReferenceId: result.referenceId,
      cvu: result.cvu,
      accountHolder: result.accountHolder,
      alias: result.alias,
      conciliationType: result.conciliationType,
      updated: new Date(),
    };

    this.digitalPaymentChange.emit(digitalPayment);
  }

  private initPayerConciliation(): void {
    const storedPayer = this.transferPayer();

    if (!storedPayer) {
      return;
    }

    this.editingPayer.set(false);
    this.generateCVUForStoredPayer(storedPayer);
  }

  private generateCVUForStoredPayer(storedPayer: TransferPayer): void {
    this.generateCVU(storedPayer.taxId ?? storedPayer.document, storedPayer);
  }

  onPayerDocumentChange(value: string | null): void {
    const document = normalizePayerDocument(value);

    this.payerDocumentInput.set(value);
    this.payerError.set(null);
    this.cvuError.set(null);
    this.clearPayerLookupTimeout();

    if (!PAYER_DOCUMENT_PATTERN.test(document) || this.resolvedPayer()?.document === document) {
      return;
    }

    const debounceMs =
      document.length === SHORT_PAYER_DOCUMENT_LENGTH
        ? SHORT_PAYER_DOCUMENT_DEBOUNCE_MS
        : PAYER_LOOKUP_DEBOUNCE_MS;

    this.payerLookupTimeout = setTimeout(() => {
      this.payerLookupTimeout = null;
      this.generateCVU(document);
    }, debounceMs);
  }

  onChangePayer(): void {
    this.payerDocumentInput.set(null);
    this.resolvedPayer.set(null);
    this.payerError.set(null);
    this.editingPayer.set(true);
  }

  private clearPayerLookupTimeout(): void {
    if (this.payerLookupTimeout) {
      clearTimeout(this.payerLookupTimeout);
      this.payerLookupTimeout = null;
    }
  }

  private applyResolvedPayer(
    payer: ResolvedTransferPayer,
    storedPayer: TransferPayer | null,
  ): void {
    const transferPayer: TransferPayer =
      payer.status === 'provided' && storedPayer
        ? storedPayer
        : { document: payer.document, taxId: payer.taxId, name: payer.name };

    this.resolvedPayer.set(payer);
    this.transferPayer.set(transferPayer);
  }

  private handlePayerError(error: unknown): boolean {
    const payerError =
      error instanceof BankTransferRequestError && error.code
        ? PAYER_FIELD_ERRORS[error.code]
        : undefined;

    if (!payerError) {
      return false;
    }

    this.editingPayer.set(true);
    this.payerError.set(payerError);
    return true;
  }

  /**
   * Generates a CVU for the transfer payment.
   * The service handles caching internally.
   */
  private async generateCVU(
    payerDocument?: string,
    storedPayer: TransferPayer | null = null,
  ): Promise<void> {
    // Cancel any pending request
    this.abortController?.abort();
    const abortController = new AbortController();
    this.abortController = abortController;

    this.cvuLoading.set(true);
    this.cvuError.set(null);

    try {
      const result = await this.bankTransferService.generateCVU({
        establishmentId: this.establishmentId(),
        customerName: this.customerName() || 'Cliente',
        amount: this.finalAmount(),
        referenceId: this.transferReferenceId(),
        payerDocument,
      });

      // Check if aborted (component destroyed or new request started)
      if (abortController.signal.aborted) {
        return;
      }

      if (result.payer) {
        this.applyResolvedPayer(result.payer, storedPayer);
      }

      this.emitCvu(result);
    } catch (error) {
      // Ignore abort errors (expected when component is destroyed)
      if (
        abortController.signal.aborted ||
        (error instanceof Error && error.name === 'AbortError')
      ) {
        return;
      }

      if (this.handlePayerError(error)) {
        return;
      }

      this.cvuError.set(
        error instanceof Error ? error.message : 'Error al generar datos de transferencia',
      );
      this.cvuDataChange.emit(null);
      this.digitalPaymentChange.emit(null);
    } finally {
      if (!abortController.signal.aborted) {
        this.cvuLoading.set(false);
      }
    }
  }

  /**
   * Updates the payment amount in ChytaPay with retry logic.
   */
  private async updateAmountWithRetry(amount: number, maxRetries = 3): Promise<void> {
    const cached = this.bankTransferService.getCachedCvuData(this.establishmentId());
    if (!cached?.referenceId) return;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        await this.bankTransferService.updateAmount({
          referenceId: cached.referenceId,
          amount,
          establishmentId: this.establishmentId(),
        });
        return;
      } catch (error) {
        if (attempt < maxRetries) {
          const delayMs = Math.pow(2, attempt - 1) * 1000;
          await new Promise(resolve => setTimeout(resolve, delayMs));
        }
      }
    }
  }

  /**
   * Retry CVU generation after an error.
   * Clears the service cache to force a new generation.
   */
  retryCVUGeneration(): void {
    const storedPayer = this.editingPayer() ? null : this.transferPayer();
    const payerDocument = this.payerDocument();

    if (this.payerConciliationEnabled() && !storedPayer && !payerDocument) {
      return;
    }

    this.bankTransferService.clearCache();
    this.cvuData.set(null);
    this.cvuError.set(null);

    if (!this.payerConciliationEnabled()) {
      this.generateCVU();
    } else if (storedPayer) {
      this.generateCVUForStoredPayer(storedPayer);
    } else {
      this.generateCVU(payerDocument ?? undefined);
    }
  }

  /**
   * Confirms the final amount with ChytaPay.
   * Should be called once before submitting the order.
   * Only applicable for ChytaPay provider (Cucuru doesn't require amount confirmation).
   */
  async confirmAmount(): Promise<void> {
    const cached = this.bankTransferService.getCachedCvuData(this.establishmentId());

    if (cached?.provider === 'chytapay') {
      await this.updateAmountWithRetry(this.finalAmount());
    }
  }
}
